import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { CalendarClock, Play, RefreshCw } from 'lucide-react';
import { LineChart } from '@nyce/admin-kit/console';
import { Button, Select, Switch } from '@nyce/admin-kit/ui';
import {
  EmptyState,
  ErrorBox,
  Kpi,
  Loading,
  Page,
  SortSelect,
  SortTh,
  TableScroller,
  Tabs,
  useAsync,
  useInterval,
  useNotice,
  useSort,
  type SortKeys,
} from '../../components/common';
import { formatCount, formatDateTime, formatDuration, formatRelative } from '../../lib/format';
import { useParam } from '../../pages/statistics/shared';
import {
  canRun,
  isRunActive,
  prodStats,
  type DatabaseRow,
  type MetricId,
  type ProdRun,
} from '../api/prodstats';
import { RunChip } from '../shared';
import { addDays, dailySeries, DAY, dayMs, typeTotals } from '../series';
import { RunDialog, ScheduleDialog } from './dialogs';
import '../prodstats.css';

const METRICS: { value: MetricId; label: string }[] = [
  { value: 'transactions', label: 'Transactions' },
  { value: 'cod', label: 'Customer deliveries' },
  { value: 'cod_lines', label: 'Customer delivery lines' },
  { value: 'shipment_refs', label: 'Shipment references' },
  { value: 'pd', label: 'Purchase deliveries' },
  { value: 'pd_lines', label: 'Purchase delivery lines' },
];

type RangeKey = '30' | '90' | '365' | 'all';
const RANGES: { value: RangeKey; label: string }[] = [
  { value: '30', label: '30 d' },
  { value: '90', label: '90 d' },
  { value: '365', label: '1 y' },
  { value: 'all', label: 'All' },
];

const POLL_MS = 3000;

const DB_SORT: SortKeys<DatabaseRow> = {
  database: { label: 'Database', value: (d) => d.environment?.name ?? d.database },
  transactions: { label: 'Transactions', value: (d) => d.counts.transactions ?? 0, desc: true },
  cod: { label: 'Customer deliveries', value: (d) => d.counts.cod ?? 0, desc: true },
  cod_lines: { label: 'Customer lines', value: (d) => d.counts.cod_lines ?? 0, desc: true },
  shipment_refs: { label: 'Shipment refs', value: (d) => d.counts.shipment_refs ?? 0, desc: true },
  pd: { label: 'Purchase deliveries', value: (d) => d.counts.pd ?? 0, desc: true },
  pd_lines: { label: 'Purchase lines', value: (d) => d.counts.pd_lines ?? 0, desc: true },
  lastDay: { label: 'Last day', value: (d) => d.lastDay, desc: true },
};

/**
 * Statistics → Production: what the nyce.logic databases produced per day (transactions per type, customer order
 * deliveries + lines, shipment references, purchase deliveries + lines), counted every night by the hub on one copy of
 * each database. The metric, range, database and archive switch live in the URL.
 */
export function ProductionPage({ scopes }: { scopes: readonly string[] }) {
  const [metricParam, setMetric] = useParam('metric', 'transactions');
  const metric = (METRICS.some((m) => m.value === metricParam) ? metricParam : 'transactions') as MetricId;
  const [rangeParam, setRange] = useParam('range', '90');
  const range = (RANGES.some((r) => r.value === rangeParam) ? rangeParam : '90') as RangeKey;
  const [database, setDatabase] = useParam('db', '');
  const [archiveParam, setArchive] = useParam('archive', '');
  const includeArchive = archiveParam === '1';
  const run = canRun(scopes);
  const notice = useNotice();
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [runOpen, setRunOpen] = useState<number | null>(null);

  const runs = useAsync(() => prodStats.runs(10), []);
  const active = (runs.data ?? []).some(isRunActive);
  useInterval(() => void runs.reload(), POLL_MS, active);
  // the counts change when a run finishes
  const doneKey = (runs.data ?? []).find((r) => !isRunActive(r))?.id ?? 0;
  const totals = useAsync(() => prodStats.totals(includeArchive), [includeArchive, doneKey]);
  const today = totals.data?.today;
  const to = today ?? '';
  const from = !today
    ? ''
    : range === 'all'
      ? (totals.data?.firstDay ?? addDays(today, -30))
      : addDays(today, -Number(range));
  const daily = useAsync(
    () =>
      from && from < to
        ? prodStats.daily({ from, to, metric, database: database || undefined, includeArchive })
        : Promise.resolve(null),
    [from, to, metric, database, includeArchive, doneKey]
  );
  const dbs = useAsync(
    () => (from && from < to ? prodStats.databases({ from, to, includeArchive }) : Promise.resolve(null)),
    [from, to, includeArchive, doneKey]
  );
  const sort = useSort(dbs.data?.databases ?? [], DB_SORT, useParam('sort', ''));
  const label = METRICS.find((m) => m.value === metric)!.label;
  const series = useMemo(
    () =>
      daily.data ? dailySeries(daily.data.rows, metric, label, metric === 'transactions', from, to) : [],
    [daily.data, metric, label, from, to]
  );
  const types = metric === 'transactions' && daily.data ? typeTotals(daily.data.rows, metric) : [];
  const dbOptions = [
    { value: '', label: 'All databases' },
    ...(dbs.data?.databases ?? [])
      .map((d) => ({
        value: d.database,
        label: d.environment ? `${d.environment.name} (${d.database})` : d.database,
      }))
      .sort((a, b) => a.label.localeCompare(b.label)),
  ];

  const start = async () => {
    try {
      const r = await prodStats.start();
      notice.success(`Run #${r.id} started`);
      await runs.reload();
    } catch (e) {
      notice.error(e instanceof Error ? e.message : String(e));
    }
  };

  const reload = () => {
    void runs.reload();
    void totals.reload();
    void daily.reload();
    void dbs.reload();
  };

  return (
    <Page
      title="Production"
      description="What the nyce.logic databases produced per day, counted every night on one copy of each database (a replica when there is one). Today is still being counted, so the views end yesterday."
      actions={
        <>
          {run && (
            <>
              <Button
                size="sm"
                variant="ghost"
                icon={<CalendarClock size={14} />}
                onClick={() => setScheduleOpen(true)}
              >
                Schedule
              </Button>
              <Button size="sm" icon={<Play size={14} />} disabled={active} onClick={() => void start()}>
                Count now
              </Button>
            </>
          )}
          <Button
            size="sm"
            variant="ghost"
            icon={<RefreshCw size={14} />}
            loading={totals.loading}
            onClick={reload}
          >
            Refresh
          </Button>
        </>
      }
    >
      {notice.element}
      <ErrorBox error={totals.error || daily.error || dbs.error || runs.error} />
      {totals.loading && !totals.data && <Loading loading />}
      {totals.data && !totals.data.firstDay && !active && (
        <EmptyState
          card
          title="Nothing counted yet."
          hint={
            run
              ? 'The first run counts each database’s history; start it with “Count now” or wait for the nightly run.'
              : 'The first nightly run counts each database’s history.'
          }
        />
      )}
      {totals.data && (
        <div className="nyk-kpis" data-testid="prod-kpis">
          {totals.data.metrics.map((m) => (
            <Kpi
              key={m.id}
              label={`${m.label} yesterday`}
              value={formatCount(m.yesterday)}
              sub={`30 d ${formatCount(m.last30)} · year ${formatCount(m.yearToDate)}`}
            />
          ))}
        </div>
      )}

      <div className="nyk-card" data-testid="prod-chart">
        <div className="nyk-card-head">
          <h2>{label} per day</h2>
          {database && <span className="nyk-ui-hint">{database}</span>}
        </div>
        <div className="nyk-toolbar ps-toolbar">
          <Tabs value={metric} onChange={setMetric} options={METRICS} aria-label="Metric" />
          <Tabs value={range} onChange={setRange} options={RANGES} aria-label="Range" />
          <Select
            aria-label="Database"
            value={database}
            options={dbOptions}
            onChange={(v) => setDatabase(v)}
            fieldClassName="ps-db-select"
          />
          {metric === 'transactions' && (
            <Switch
              checked={includeArchive}
              onChange={(on) => setArchive(on ? '1' : '')}
              label="Include ACCOUNT_RENDERED (archiving)"
            />
          )}
        </div>
        {from && (
          <LineChart
            title={`${label} per day`}
            from={dayMs(from)}
            to={dayMs(to) - DAY}
            stepMs={DAY}
            format={formatCount}
            series={series}
            empty="Nothing counted in this range."
          />
        )}
        {types.length > 0 && (
          <p className="nyk-ui-hint ps-types" data-testid="prod-types">
            {types.map((t) => `${t.typeCode || 'No type'} ${formatCount(t.count)}`).join(' · ')}
          </p>
        )}
      </div>

      <div className="nyk-card">
        <div className="nyk-card-head">
          <h2>Per database</h2>
          <span className="nyk-ui-hint">
            {from && `${from} – ${addDays(to, -1)}`} · {dbs.data?.databases.length ?? 0}
          </span>
        </div>
        {dbs.loading && !dbs.data ? (
          <Loading loading />
        ) : !dbs.data?.databases.length ? (
          <EmptyState title="Nothing counted in this range." />
        ) : (
          <>
            <div className="nyk-toolbar">
              <SortSelect sort={sort} />
            </div>
            <TableScroller>
              <table
                className="nyk-ui-table nyk-cards"
                aria-label="Production per database"
                data-testid="prod-databases"
              >
                <thead>
                  <tr>
                    <SortTh sort={sort} by="database" />
                    <SortTh sort={sort} by="transactions" className="ps-num" />
                    <SortTh sort={sort} by="cod" className="ps-num" />
                    <SortTh sort={sort} by="cod_lines" className="ps-num" />
                    <SortTh sort={sort} by="shipment_refs" className="ps-num" />
                    <SortTh sort={sort} by="pd" className="ps-num" />
                    <SortTh sort={sort} by="pd_lines" className="ps-num" />
                    <SortTh sort={sort} by="lastDay" />
                  </tr>
                </thead>
                <tbody>
                  {sort.rows.map((d) => (
                    <tr key={d.database}>
                      <td className="nyk-card-title">
                        <button
                          type="button"
                          className="nyk-link ps-db"
                          onClick={() => setDatabase(d.database)}
                        >
                          {d.environment?.name ?? d.database}
                        </button>
                        {d.environment && <div className="nyk-ui-hint">{d.database}</div>}
                      </td>
                      {(
                        ['transactions', 'cod', 'cod_lines', 'shipment_refs', 'pd', 'pd_lines'] as MetricId[]
                      ).map((m) => (
                        <td key={m} data-label={DB_SORT[m].label} className="ps-num">
                          {formatCount(d.counts[m] ?? 0)}
                        </td>
                      ))}
                      <td data-label="Last day">{d.lastDay ?? '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </TableScroller>
          </>
        )}
      </div>

      <RunsCard
        runs={runs.data ?? []}
        loading={runs.loading}
        next={totals.data?.schedule}
        onOpen={setRunOpen}
      />
      {runOpen != null && (
        <RunDialog id={runOpen} canCancel={run} onClose={() => setRunOpen(null)} onChanged={reload} />
      )}
      {scheduleOpen && (
        <ScheduleDialog
          onClose={() => setScheduleOpen(false)}
          onSaved={(s) => {
            notice.success(
              s.enabled ? `Next count ${formatDateTime(s.nextRunAt)}` : 'Nightly count turned off'
            );
            void totals.reload();
          }}
        />
      )}
    </Page>
  );
}

function RunsCard({
  runs,
  loading,
  next,
  onOpen,
}: {
  runs: ProdRun[];
  loading: boolean;
  next?: { enabled: boolean; nextRunAt: string | null };
  onOpen: (id: number) => void;
}) {
  return (
    <div className="nyk-card" data-testid="prod-runs">
      <div className="nyk-card-head">
        <h2>Runs</h2>
        <span className="nyk-ui-hint">
          {next?.enabled ? `next ${formatDateTime(next.nextRunAt)}` : next ? 'nightly count off' : ''}
        </span>
      </div>
      {loading && !runs.length ? (
        <Loading loading />
      ) : !runs.length ? (
        <EmptyState title="No runs yet." />
      ) : (
        <TableScroller>
          <table className="nyk-ui-table nyk-cards" aria-label="Production statistics runs">
            <thead>
              <tr>
                <th>Run</th>
                <th>Status</th>
                <th>Started</th>
                <th>Took</th>
                <th className="ps-num">Databases</th>
                <th className="ps-num">Rows added</th>
                <th aria-label="Details" />
              </tr>
            </thead>
            <tbody>
              {runs.map((r) => (
                <tr key={r.id}>
                  <td className="nyk-card-title">
                    #{r.id}
                    <div className="nyk-ui-hint">{r.scheduled ? 'nightly' : r.createdBy}</div>
                  </td>
                  <td data-label="Status">
                    <RunChip run={r} />
                    {r.error && <div className="nyk-ui-hint">{r.error}</div>}
                  </td>
                  <td data-label="Started">{r.startedAt ? formatRelative(r.startedAt) : 'queued'}</td>
                  <td data-label="Took">
                    {r.startedAt && r.finishedAt
                      ? formatDuration(Date.parse(r.finishedAt) - Date.parse(r.startedAt))
                      : '—'}
                  </td>
                  <td data-label="Databases" className="ps-num">
                    {r.databasesDone}/{r.databasesTotal}
                  </td>
                  <td data-label="Rows added" className="ps-num">
                    {formatCount(r.rowsCounted)}
                  </td>
                  <td>
                    <Button size="sm" variant="ghost" onClick={() => onOpen(r.id)}>
                      Details
                    </Button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableScroller>
      )}
      <p className="nyk-ui-hint">
        Each run counts the rows added since the last one (by id), so a database that fails is caught up by
        the next run. The databases are those on the registered{' '}
        <Link to="/databases">production database servers</Link>.
      </p>
    </div>
  );
}
